import { useMemo, useCallback } from 'react';
import { useRoutes } from 'react-router-dom';

import { AuthStatus } from '@app/auth/api/auth.type';
import { AuthUserSchema } from '@app/auth/api/auth.dto';
import { ERole, ETeamMemberRole } from '@app/swagger-types';

import { useStore } from '@app/store/useStore.hook';

import { TypedRouteObject } from '../types';

export const useFiltredRoutes = <
  TAuthStatus extends AuthStatus,
  TRole extends ERole[],
  TTeamMemberRole extends ETeamMemberRole[]
>(
  routes: TypedRouteObject<TAuthStatus, TRole, TTeamMemberRole>[]
) => {
  const { currentUser, authStatus } = useStore(({ currentUser, authStatus }) => ({
    currentUser,
    authStatus,
  }));

  const guardCheck = useCallback(
    <R extends TypedRouteObject<TAuthStatus, TRole, TTeamMemberRole>>({
      route,
      authStatus,
      currentUser,
    }: {
      route: R;
      currentUser: AuthUserSchema | null;
      authStatus: AuthStatus;
    }) => {
      {
        // AUTH STATUS GUARD
        if (route.authStatus && route.authStatus !== authStatus) {
          return false;
        }

        // ROLE GUARD
        if (route.role) {
          const userRole = currentUser?.role;
          if (!userRole) {
            return false;
          }

          return route.role.includes(userRole);
        }

        // TEAM MEMBER ROLE GUARD
        if (route.teamMemberRole) {
          const userTeamMemberRole = currentUser?.teamMemberRole;
          // If the route role is specified and the user does not have a role or his role does not match
          if (userTeamMemberRole === ETeamMemberRole.NONE || userTeamMemberRole === ETeamMemberRole.OWNER) {
            return true;
          }
          if (userTeamMemberRole) {
            return route.teamMemberRole.includes(userTeamMemberRole);
          }
        }

        return true;
      }
    },
    []
  );

  const filtredRoutes = useMemo(() => {
    const deepFilter = (routeObjects: TypedRouteObject<TAuthStatus, TRole, TTeamMemberRole>[]) => {
      const filteredRoutes: TypedRouteObject<TAuthStatus, TRole, TTeamMemberRole>[] = routeObjects.reduce<
        TypedRouteObject<TAuthStatus, TRole, TTeamMemberRole>[]
      >((filteredRoutes, currentRoute) => {
        if (!guardCheck({ route: currentRoute, currentUser, authStatus })) {
          return filteredRoutes;
        }

        if (currentRoute.children) {
          return [...filteredRoutes, { ...currentRoute, children: deepFilter(currentRoute.children) }];
        }

        return [...filteredRoutes, currentRoute];
      }, []);

      return filteredRoutes;
    };

    return deepFilter(routes);
  }, [authStatus, currentUser, guardCheck, routes]);

  return useRoutes(filtredRoutes);
};
